import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
    isLibraryPolicyViolation, approvalPlanFor, APPROVAL_KINDS, updateBodyFor,
    markApproved, wasApproved, messageForFailure, findPolicyForAdvice
} from "../../../classes/libraryPolicyApproval"
import { setLibraryApproval, libraryApprovalCompleted } from "../../../state/actions/app"

const PHASES = { IDLE: "idle", SENDING: "sending", FAILED: "failed" }

// The two halves of approving are deliberately asymmetric, as the spec asks:
// creating a policy is a decision with fields to fill in, so it opens a modal;
// extending one that already exists only widens its version range, so it just
// happens - there is nothing to ask.
const ApproveLibrary = ({ advice, version }) => {
    const dispatch = useDispatch()
    const teamUuid = useSelector((state) => {
        const projectPayload = state.projectInfo.projectInfo
        const reportPayload = state.projectInfo.report
        if (projectPayload && projectPayload.teamUuid) return projectPayload.teamUuid
        if (reportPayload && reportPayload.teamUuid) return reportPayload.teamUuid
        return null
    })
    const reportUuid = useSelector((state) => (state.projectInfo.report ? state.projectInfo.report.uuid : null))
    // The create path is completed inside the modal, which lives in another
    // part of the tree and marks the approval in sessionStorage - and nothing
    // re-renders when sessionStorage changes. This is how this button hears
    // that the approval it opened was the one that went through.
    const wasApprovedInModal = useSelector((state) => {
        const completed = state.libraryApprovalState.lastCompletedApproval
        if (!completed) return false
        return completed.adviceId === advice.id && completed.version === version
    })
    const [phase, setPhase] = useState(PHASES.IDLE)
    const [failureMessage, setFailureMessage] = useState(null)

    // A failure belonging to one advice must never be shown against another:
    // if the parent re-renders this component in place with a different advice
    // instead of remounting it, reset local state so the next render starts
    // clean for the new advice.
    useEffect(() => {
        setPhase(PHASES.IDLE)
        setFailureMessage(null)
    }, [advice.id])

    // Both payloads now carry teamUuid, but the guard stays: showing nothing
    // when it is missing is deliberate, because guessing a scope would approve
    // at account level, which per the API deactivates matching team policies.
    if (!isLibraryPolicyViolation(advice)) return null
    if (!teamUuid) return null
    if (!version) return null

    if (wasApprovedInModal || wasApproved(reportUuid, advice.id, version)) {
        return (
            <div className="library-approval">
                <button className="btn btn-primary btn-action" disabled>Approved</button>
            </div>
        )
    }

    const plan = approvalPlanFor(advice, version)

    const openCreateModal = () => {
        dispatch(setLibraryApproval({ advice: advice, version: version, teamUuid: teamUuid }))
    }

    const extendExistingPolicy = async (policyUuid) => {
        setPhase(PHASES.SENDING)
        try {
            const libraryPolicies = window.meterian.teams.libraryPolicies
            // update() is a full replace, and the advice carries only the
            // server's own MESSAGE, not the policy's description or active
            // flag - echoing those from the advice would destroy them. Read
            // the real policy first and build the request from that.
            let policy = null
            try {
                policy = await libraryPolicies.get(teamUuid, policyUuid)
            }
            catch (getFailure) {
                // advice.sourceID said a policy existed at this uuid and the
                // server disagrees. Creating is the safe reading - but
                // creating is a modal flow now, with fields only the user can
                // fill in, so this hands over to it rather than sending
                // anything. If a policy did exist after all, the create 409s
                // in the modal, where the user can act on it.
                if (getFailure && getFailure.status === 404) {
                    setPhase(PHASES.IDLE)
                    openCreateModal()
                    return
                }
                throw getFailure
            }
            const body = updateBodyFor(policy, version)
            if (!body.isUnchanged) {
                await libraryPolicies.update(teamUuid, policyUuid, {
                    versionRange: body.versionRange,
                    severity: body.severity,
                    description: body.description,
                    active: body.active
                })
            }
            markApproved(reportUuid, advice.id, version)
            setPhase(PHASES.IDLE)
            // The create path signals completion via this same action from the
            // modal (see approve_library.js). This is the update path's
            // equivalent: both must announce completion identically, because
            // App listens for one signal to raise the alert and glow, not two.
            dispatch(libraryApprovalCompleted(advice.id, version))
        }
        catch (failure) {
            setFailureMessage(messageForFailure(failure))
            setPhase(PHASES.FAILED)
        }
    }

    // A CREATE plan only means the ADVICE carried no policy uuid - sourceID is
    // null on every policy-violation advice, so it never carries one, even when
    // a policy governs the library. Creating on that basis would try to add a
    // second policy for a library that already has one; the operation is an
    // update of the range on the existing policy. The list is only fetched on
    // the create branch, so the path that already knows its uuid still sends
    // exactly one request.
    const approve = async () => {
        setFailureMessage(null)
        if (plan.kind !== APPROVAL_KINDS.CREATE) {
            await extendExistingPolicy(plan.policyUuid)
            return
        }

        setPhase(PHASES.SENDING)
        let existingPolicy = null
        try {
            const policies = await window.meterian.teams.libraryPolicies.all(teamUuid)
            existingPolicy = findPolicyForAdvice(policies, advice)
        }
        catch (lookupFailure) {
            // The lookup stands in for a field the advice should have carried,
            // so failing it must not block the approval - fall through to the
            // create flow, which is what this branch did before the lookup
            // existed. If a policy did exist after all, create 409s in the
            // modal, where the user can act on it.
            existingPolicy = null
        }

        // Genuinely nothing to widen - this is the first approval for this
        // library, which is a decision with fields to fill in.
        if (!existingPolicy) {
            setPhase(PHASES.IDLE)
            openCreateModal()
            return
        }
        await extendExistingPolicy(existingPolicy.uuid)
    }

    return (
        <div className="library-approval">
            <button className="btn btn-primary btn-action issue-approve"
                disabled={phase === PHASES.SENDING}
                onClick={approve}>
                {phase === PHASES.SENDING ? "Approving…" : "Approve"}
            </button>
            {phase === PHASES.FAILED ? <p className="library-approval-failure">{failureMessage}</p> : null}
        </div>
    )
}

export default ApproveLibrary
